import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TakeUntilDestroy } from '../../../core/component/take-until-destroy';
import { BottomPanelService } from '../../services/bottom-panel.service';
import { Ui } from '../../../shared/enum/ui/ui';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions } from 'ag-grid-community';
import { AgGridDataSource } from '../../utilities/ag-grid-data-source';
import { GraphicListColumn } from '../../models/ag-grid-list-column.model';
import { filter, take, takeUntil } from 'rxjs/operators';
import { AgGridColumnsTransformService } from '../../services/ag-grid-columns-transform.service';
import { GridConfig } from '../../models/grid-config.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { AgGridUtil } from '../../utilities/ag-grid-util';
import { AgGridNotifyService } from '../../services/ag-grid-notify.service';
import { AgGridListViewService } from '../../services/ag-grid-list-view.service';
import { SelectionNotifyService } from '../../../shared/service/selection-notify.service';
import { CollapseStatus } from '../../../shared/enum/ui/collapse-status.enum';
import { JustMapExtentService } from '../../services/just-map-extent.service';
import { MainMapService } from '../../../shared/service/main-map.service';
import { TranslateService } from '@ngx-translate/core';
import { DetailViewFeatureParams } from '../../../component/detail-view/detail-view.model';
import { SelectionService } from '../../../shared/service/selection.service';

@Component({
    selector: 'ag-grid-server-side',
    templateUrl: './ag-grid-server-side.component.html',
    styleUrls: ['./ag-grid-server-side.component.scss'],
    standalone: false
})
export class AgGridServerSideComponent extends TakeUntilDestroy implements OnInit, OnDestroy {
  @ViewChild('agGrid') agGrid: AgGridAngular;

  @Input() multiselect = false;
  @Input() disableSelection = false;
  @Input() hideClearFiltersButton = false;
  @Input() defaultFilterModel: any;
  @Input() rowClassRules: {
    [cssClassName: string]: ((params: any) => boolean) | string;
  };

  @Input() defaultSortModel: {
    colId: string;
    sort: string;
  }[];

  // The ID of this TAB
  @Input() tabID: string;
  // The Index of this TAB
  @Input() tabIndex: number;
  // sort by orderByFields
  @Input() orderByFields: string[];
  @Input() disableAutoSize = false;
  @Input() fitColumnsSize: boolean;
  @Input() hasGeometry: boolean;

  // serverside
  public gridOptions: GridOptions;
  public defaultColDef: ColDef;
  public columnDefs: any[] = [];
  floatingFilter = true; // default value

  private bottomPanelCollapseState: Ui.CollapseStatus;
  private dataSource: AgGridDataSource;
  private isFirstLoad = true;
  private refreshPending = false;
  private isReloading = false;
  private isDestroyed = false;
  private gridReady = false;
  private columns: GraphicListColumn[];
  private isGridSelectionMode = false;

  private isDefaultFiltersSubject: BehaviorSubject<boolean> = new BehaviorSubject(undefined);

  get isOpen(): boolean {
    return this.bottomPanelCollapseState === Ui.CollapseStatus.open;
  }

  get isDefaultFilters$(): Observable<boolean> {
    return this.isDefaultFiltersSubject.asObservable();
  }

  get hasFilters(): boolean {
    if (!this.agGrid || !this.agGrid.api) {
      return false;
    }
    const filterModel = AgGridUtil.getGridFilterModel(this.agGrid.api);
    return Object.keys(filterModel).length > 0;
  }

  get justMapExtent$() {
    return this.justMapExtentService.justMapExtent$;
  }

  constructor(
    private readonly bottomPanelService: BottomPanelService,
    private readonly columnsTransformService: AgGridColumnsTransformService,
    private agGridlistViewService: AgGridListViewService,
    private agGridNotifyService: AgGridNotifyService,
    private readonly selectionNotifyService: SelectionNotifyService,
    private readonly justMapExtentService: JustMapExtentService,
    private readonly mainMapService: MainMapService,
    private translate: TranslateService,
    private selectionService: SelectionService
  ) {
    super();
  }

  ngOnInit() {
    // define default column definition, which will apply to all columns
    this.defaultColDef = {
      sortable: true,
      suppressHeaderMenuButton: true,
      resizable: true,
      minWidth: 120,
      hide: false,
    };

    // define the grid options
    this.gridOptions = {
      pagination: true,
      paginationAutoPageSize: true,
      paginationPageSize: 20,
      cacheBlockSize: 20,
      maxBlocksInCache: 20,
      rowSelection: {
        mode: this.multiselect ? 'multiRow' : 'singleRow',
        checkboxes: false,
        enableSelectionWithoutKeys: true,
        enableClickSelection: true,
      },
      rowModelType: 'serverSide',
      getRowId: data => data.data.OBJECTID.toString(),
      defaultColDef: this.defaultColDef,
      suppressColumnVirtualisation: true,
      suppressCellFocus: true,
      blockLoadDebounceMillis: 500,
      rowClassRules: this.rowClassRules,
    };

    this.initializeGridColumns();

    this.agGridNotifyService.onReload.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      setTimeout(() => {
        if (this.isDestroyed) {
          return;
        }
        if (this.hasFilters) {
          // If has filters, need to clear columns and wait for a onFilterChange cycle
          // to create the new columns and bind the datasource
          this.agGrid.api.setGridOption('columnDefs', []);
          this.isReloading = true;
        } else {
          this.agGrid.api.setGridOption('columnDefs', []);
          this.recreateGrid();
        }
      });
    });

    this.justMapExtentService.justMapExtent$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      setTimeout(() => {
        if (!this.isDestroyed) {
          this.refreshData();
        }
      });
    });

    this.mainMapService.onExtentChangeSubject.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      if (this.isOpen && this.justMapExtentService.justMapExtent) {
        setTimeout(() => {
          if (!this.isDestroyed) {
            this.refreshData();
          }
        });
      } else {
        this.refreshPending = true;
      }
    });

    this.agGridNotifyService.onRefreshData.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      if (this.isOpen) {
        setTimeout(() => {
          if (!this.isDestroyed) {
            this.refreshData();
          }
        });
      } else {
        this.refreshPending = true;
      }
    });

    this.agGridNotifyService.onUpdateFilters.pipe(takeUntil(this.unsubscribe$)).subscribe(filterModel => {
      setTimeout(() => {
        if (!this.isDestroyed) {
          this.setTableFilters(filterModel);
        }
      });
    });

    // listen to selection change on the map
    this.selectionNotifyService.onSelectionChangeSubject
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(() => this.gridReady)
      )
      .subscribe(selection => {
        if (this.agGridlistViewService.activeTabID !== this.tabID) return;

        if (this.hasGeometry) {
          this.dataSource.selection = selection;
          if (!this.isGridSelectionMode) {
            this.syncSelectionFromQuery();
          } else {
            this.isGridSelectionMode = false;
          }
        }
      });

    this.agGridNotifyService.onSelectedTabChanged.pipe(takeUntil(this.unsubscribe$)).subscribe(selectedTabID => {
      if (this.fitColumnsSize) {
        this.agGrid.api.sizeColumnsToFit();
      }
    });

    this.bottomPanelService.state$.pipe(takeUntil(this.unsubscribe$)).subscribe(bottomPanelCollapseState => {
      this.bottomPanelCollapseState = bottomPanelCollapseState.collapseStatus;
      if (this.gridReady && this.isOpen) {
        // selection must be set after bottom panel animation ends to get the right page size
        setTimeout(() => {
          if (!this.isDestroyed) {
            if (this.refreshPending) {
              this.refreshData();
            }
            this.syncSelectionFromQuery();
            this.autosizeColumns();
          }
        }, 1000);
      } else if (
        bottomPanelCollapseState.collapseStatus === CollapseStatus.collapsed &&
        this.selectionNotifyService.getSelection().length > 0
      ) {
        if (this.agGridlistViewService.activeTabID !== this.tabID) {
          return;
        }
        this.showDetailView(this.selectionNotifyService.getSelectionFeature());
      }
    });

    // listen on language change
    this.translate.onLangChange.subscribe(() => {
      this.recreateGrid();
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  /**
   * @function onGridReady
   * @see https://www.ag-grid.com/javascript-grid-events/
   * @description The grid has initialised. Content not loaded yet. -> FilterModel may be changed from here on...
   * @param params
   */
  onGridReady(params) {
    if (this.agGrid.api !== params.api) console.warn(`onGridReady(params) -> this.agGrid.api !== params.api`);
    this.gridReady = true;
    this.initializeDatasource();
    this.setIsDefaultFilters();
  }

  onSelectionChanged(params) {
    // list just a table, feature have not geometry
    if (!this.hasGeometry) return;

    if (!this.isGridSelectionMode) return;
    // get agGrid selection, only contains nodes selected in memory
    const selectedObjectIds = this.agGrid.api.getSelectedRows().map(o => o.OBJECTID);

    this.handleSingleSelectChanged(selectedObjectIds, params);
  }

  handleSingleSelectChanged(selectedObjectIds: number[], params) {
    if (selectedObjectIds.length > 0) {
      this.agGridlistViewService.selectFeaturesOnMap(selectedObjectIds, this.tabID);
    } else {
      this.selectionNotifyService.clearSelection();
      this.selectionService.removeAllSelectedGraphics();
    }
  }

  /**
   * @function onFilterChanged
   * @see https://www.ag-grid.com/javascript-grid-events/
   * @description Triggered from grid when user changed + applied column filterd, notifies queryString change
   * @param params
   */
  onFilterChanged(params): void {
    if (this.isReloading) {
      // Reloading grid, triggers an onFilterChange that needs to be ignored
      this.recreateGrid();
      this.isReloading = false;
    } else {
      this.setIsDefaultFilters();
    }
  }

  onRowClicked(param) {
    this.isGridSelectionMode = true;
  }

  public refreshData() {
    this.agGrid.api.refreshServerSide();
    this.refreshPending = false;
  }

  public clearAllFilters() {
    const filterModel = AgGridUtil.getGridFilterModel(this.agGrid.api);
    if (filterModel && Object.keys(filterModel).length !== 0) {
      this.agGrid.api.setFilterModel(null);
    }
  }

  /**
   * @function onGetRowDataFinished
   * @descripiton: Triggered from ag-grid-data-source after calling getRows.
   * Hook for process needed after data is loaded
   * */
  onGetRowDataFinished() {
    if (!this.gridReady || !this.isFirstLoad) {
      return;
    }
    this.isFirstLoad = false;
    this.autosizeColumns();
    this.syncSelectionFromQuery();
  }

  toggleJustMine(checked = !this.justMapExtentService.justMapExtent) {
    this.justMapExtentService.set(checked);
  }

  /* private methods */

  private initializeDatasource() {
    const gridConfig = {
      columns: this.columns,
      filterModel: this.defaultFilterModel,
      sortModel: this.defaultSortModel,
      onGetRowDataFinished: () => {
        this.onGetRowDataFinished();
      },
    } as GridConfig;

    const api = this.agGrid.api;

    this.dataSource = new AgGridDataSource(gridConfig, this.tabID, this.agGridlistViewService, []);

    api.setGridOption('serverSideDatasource', this.dataSource);

    if (gridConfig.filterModel) {
      AgGridUtil.setGridFilterModel(api, gridConfig.filterModel, true);
    }

    if (gridConfig.sortModel) {
      this.agGrid.api.applyColumnState({
        state: gridConfig.sortModel,
      });
    }
  }

  private initializeGridColumns() {
    if (!this.tabID) return;

    const featuresLyr = this.tabID;
    const tabConfig = this.agGridlistViewService.tabsList.filter(f => f.tabLayer.url === featuresLyr)[0];
    this.columns = [];
    tabConfig.columns.forEach(col => {
      this.columns.push(this.columnsTransformService.buildColumnFromDef(col));
    });

    this.columnDefs = this.columnsTransformService.transformToColumnDefs(this.columns, this.floatingFilter, params =>
      this.getDomain(params)
    );
  }

  private setTableFilters(filterModel: any, forceReload: boolean = false) {
    // update column filters if changes if out of sync
    if (
      !AgGridUtil.setGridFilterModel(this.agGrid.api, filterModel, forceReload) // fires api.onFilterChanged() if true
    ) {
      return; // no server hit
    }
    // page to first selected node
    const selectedRowIndexes = this.agGrid.api.getSelectedNodes().map(o => o.rowIndex);
    this.pageToSelection(selectedRowIndexes);
  }

  private getDomain(params) {
    if (this.dataSource) {
      this.dataSource
        .loadDomainValues(params)
        .pipe(take(1))
        .subscribe(domainValues => {
          params.success(domainValues);
        });
    } else {
      params.fail('no datasource');
    }
  }

  private autosizeColumns() {
    if (!this.disableAutoSize && !this.fitColumnsSize) {
      // only autosize columns without any width definitions
      const columnsWithoutWidthDefininition = this.columnDefs.filter(c => c.width === undefined).map(c => c.field);
      this.agGrid.api.autoSizeColumns(columnsWithoutWidthDefininition);
    }
  }

  // Sync from query
  private syncSelectionFromQuery() {
    if (this.isOpen) {
      const selection = this.selectionNotifyService.getSelection();
      this.setTableSelection(selection);
    }
  }

  private setTableSelection(selection: number[]) {
    if (!selection.length) {
      this.clearTableSelection(this.agGrid.api);
    } else {
      // try to select from cache
      const selectedRowIndexes = this.getSelectionRowIndexFromCache(selection);

      if (selectedRowIndexes.length) {
        // at least one item selected was found in the cache
        // page to first selected node
        this.pageToSelection(selectedRowIndexes);
      } else {
        this.setTableSelectionServerSide(selection);
      }
    }
  }

  private pageToSelection(indexes: number[]) {
    // navigate to first selected row...
    if (indexes.length === 0) {
      return;
    }

    // index is zero based
    const index = indexes[0] + 1;

    const pageNumber = Math.ceil(index / this.agGrid.api.paginationGetPageSize()) - 1;
    const currentPage = this.agGrid.api.paginationGetCurrentPage();

    if (pageNumber !== currentPage) {
      this.agGrid.api.paginationGoToPage(pageNumber);
    }
  }

  private clearTableSelection(api: GridApi) {
    if (!api) {
      console.warn('clearTableSelection no grid instance');
      return;
    }
    api.deselectAll();
  }

  private getSelectionRowIndexFromCache(selection: number[]): number[] {
    const selectedRowIndexes = [];
    this.agGrid.api.forEachNode(node => {
      if (node.data) {
        const select = selection.includes(node.data.OBJECTID.toString());
        node.setSelected(select);
        if (select) {
          selectedRowIndexes.push(node.rowIndex);
        }
      }
    });
    return selectedRowIndexes;
  }

  private setTableSelectionServerSide(selection: number[]) {
    if (selection.length > 0 && this.isOpen && this.dataSource) {
      this.dataSource
        .getRowIndex(selection[0], this.agGrid.api.getColumnState(), this.agGrid.api.getDisplayedRowCount())
        .subscribe(featureIndex => {
          if (featureIndex === -1) {
            return;
          }
          this.pageToSelection([featureIndex]);
        });
    }
  }

  private recreateGrid() {
    this.initializeGridColumns();
    // Ensure internals agGridApi has the right columnDefs
    this.agGrid.api.setGridOption('columnDefs', this.columnDefs);
    this.initializeDatasource();
  }

  private setIsDefaultFilters(): void {
    this.isDefaultFiltersSubject.next(!this.hasFilters);
  }

  private showDetailView(selectedFeature: DetailViewFeatureParams) {
    this.mainMapService.handleNavigationByDetailView(
      selectedFeature.currentArbeitsBereichLyrs[0].esriLayer.id,
      selectedFeature
    );
  }
}
